// @flow
import memoize from 'memoize-one';

import {
  contactbook,
  type FavoriteContact,
} from '@gandi/redux-resource-contactbook';
import { mapTags, useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectHasDomainTransfer } from '../../modules/selectors';

import type { FieldSchema } from './DomainContactForm';
import { SwitchRenderer } from './SwitchRenderer';
import type { FormValues } from './types';

// Describes the contact object in the contactbook API.
// The elements are looked up using the name field, make sure that they are unique within this list.
export const useDomainContactFields: (
  isEdition?: boolean,
) => Array<FieldSchema> = isEdition => {
  const { __ } = useTranslator();
  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);

  const schema: Array<FieldSchema> = [
    {
      name: 'id',
      type: 'string',
      label: __('id'),
      required: true,
    },
    {
      name: 'type',
      type: 'oneOf',
      isReadOnly: isEdition,
      required: !isEdition,
      label: __('Type'),
      values: [
        {
          value: 'company',
          label: __('Company'),
        },
        {
          value: 'publicbody',
          label: __('Public Body'),
        },
        {
          value: 'association',
          label: __('Association'),
        },
        {
          value: 'individual',
          label: __('Individual'),
        },
      ],
    },
    {
      name: 'orgname',
      type: 'string',
      label: __('Organization name'),
    },
    {
      name: 'given',
      type: 'string',
      label: __('Given name'),
      required: true,
    },
    {
      name: 'family',
      type: 'string',
      label: __('Family name'),
      required: true,
    },
    {
      name: 'country',
      type: 'country',
      label: __('Country'),
    },
    {
      name: 'streetaddr',
      type: 'string',
      label: __('Street address'),
    },
    {
      name: 'city',
      type: 'string',
      label: __('City'),
    },
    {
      name: 'state',
      type: 'state',
      label: __('State'),
      placeholder: __('The state or province'),
    },
    {
      name: 'zip',
      type: 'zip',
      label: __('Zip code'),
    },
    {
      name: 'email',
      type: 'email',
      label: __('Email'),
      required: true,
    },
    {
      name: 'phone',
      type: 'phone',
      label: __('Phone number'),
      required: true,
    },
    {
      name: 'fax',
      type: 'phone',
      label: __('Fax number'),
    },
    {
      name: 'mail_obfuscated',
      type: 'boolean',
      label: __('Email address privacy'),
      sublabel: __(
        "Hide email address in Gandi's public WHOIS database and at the registry.",
      ),
      help: mapTags(
        __(
          'We take care of forwarding email to your address. ' +
            '[[whoisPrivacyLink:Learn more]].\n' +
            'If you deactivate the option, you agree to your real email ' +
            'address be stored in the registry database.',
        ),
        ({ whoisPrivacyLink }) => ({
          whoisPrivacyLink: (
            <a
              href={__(
                'https://docs.gandi.net/en/domain_names/common_operations/whois_privacy.html',
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {whoisPrivacyLink}
            </a>
          ),
        }),
      ),
      renderer: SwitchRenderer,
    },
    {
      name: 'data_obfuscated',
      type: 'boolean',
      label: __('Personal data privacy'),
      sublabel: __("Hide personnal data in Gandi's public WHOIS database."),
      help: mapTags(
        __(
          'Some extensions protect data at the registry level. ' +
            '[[tldListLink:See which ones]].\n' +
            'If you deactivate the option, you agree to make all your data ' +
            ' public. [[whoisPrivacyLink:Learn more]].',
        ),
        ({ tldListLink, whoisPrivacyLink }) => ({
          tldListLink: (
            <a
              href={__(
                'https://docs.gandi.net/en/domain_names/common_operations/whois_privacy.html',
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {tldListLink}
            </a>
          ),
          whoisPrivacyLink: (
            <a
              href={__(
                'https://docs.gandi.net/en/domain_names/common_operations/whois_privacy.html',
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {whoisPrivacyLink}
            </a>
          ),
        }),
      ),
      renderer: SwitchRenderer,
    },
  ];

  if (hasDomainTransfer)
    schema.push({
      name: 'send_invitation',
      type: 'boolean',
      sublabel: __(
        'Email an invitation to the contact, allowing them to manage the domain.',
      ),
      help: (
        <>
          {mapTags(
            __(
              'Contacts must accept the invitation before they ' +
                'can view and manage the domains [[docLink:(learn more)]]. ',
            ),
            ({ docLink }) => ({
              docLink: (
                <a
                  href={__(
                    'https://docs.gandi.net/en/domain_names/common_operations/manage_domain_contacts.html',
                  )}
                  target="_blank"
                  rel="noopener"
                >
                  {docLink}
                </a>
              ),
            }),
          )}
          <br />
          {__(
            'Registries may take some time to update the information on their side.',
          )}
        </>
      ),
    });

  return schema;
};

// **********************************************

// Describes how the form will be displayed

export const formSection2 = {
  name: 'form__section2',
  type: 'form__section',
  children: [
    { name: 'type' },
    { name: 'orgname' },
    { name: 'given' },
    { name: 'family' },
    {
      name: 'country',
    },
    {
      name: 'state',
    },
    {
      name: 'streetaddr',
    },
    {
      name: 'streetaddr2',
    },
    {
      name: 'city',
    },
    { name: 'zip' },
    { name: 'email' },
    { name: 'phone' },
    { name: 'fax' },
    { name: 'mail_obfuscated' },
    { name: 'data_obfuscated' },
    { name: 'send_invitation' },
  ],
};

const getInitialValuesImpl = (
  canWriteContactBook: boolean,
  contact: FavoriteContact | void,
): FormValues => {
  const safeContact = contact || {};

  return {
    ...contactbook.defaultContactValue,
    ...safeContact,
    saveToContactbook: canWriteContactBook,
  };
};

export const getInitialValues: any = memoize(getInitialValuesImpl);

export function visitFormNodes(node: any, transform: any => any): any {
  const newNode = transform(node);

  if (newNode.children) {
    newNode.children = newNode.children.map(child =>
      visitFormNodes(child, transform),
    );
  }

  return newNode;
}
